/* ===================== REPORTS =====================
   The dashboard's Reports screen (sidebar key `reports`, open to all
   roles). Two live sections, no demo rows:

   1. Consultation Reports — the seeker's COMPLETED sessions off the
      page-level GET /bookings read, each with the astrologer's
      post-session note when one was written.
   2. Birth Chart Analysis — the full VedAstro HoroscopePredictions
      report (~100+ classical readings) computed from the profile's
      birth details, filterable by life area. Gated behind having a
      birth date on file, with skeletons while loading and a retry
      state on failure. */
import { useMemo, useState } from 'react'
import { appUrl } from '@astro/config'
import { canAccessTab } from '../roles'
import { useHoroscopePredictions } from '../useDashboard'

/* Predictions render in pages of this size — 130 cards at once is a wall. */
const PAGE_SIZE = 12

/** "EvilPlanetsInCancer_HeartDeformity" → "Heart Deformity" (last segment, camel-case split). */
function humaniseName(name) {
  const seg = String(name || '').split('_').pop() || ''
  return seg
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([A-Z0-9])([A-Z][a-z])/g, '$1 $2')
    .trim()
}

/** "MedicalAstrology" → "Medical Astrology". */
const readableTag = (t) => String(t).replace(/([a-z])([A-Z])/g, '$1 $2')

/* Life-area filter chips — VedAstro tags bucketed into reader-friendly
   groups. Tags outside every bucket still show under "All". */
const REPORT_GROUPS = [
  { key: 'personality', label: 'Personality', tags: ['Character', 'Personality', 'Emotions', 'Intelligence', 'Skills', 'Talents', 'MentalHealth', 'Arts', 'Creativity'] },
  { key: 'career', label: 'Career & Wealth', tags: ['Career', 'Finance', 'Wealth', 'Reputation', 'Education', 'Learning'] },
  { key: 'love', label: 'Love & Family', tags: ['Marriage', 'Romance', 'Family', 'Children', 'Parents', 'Siblings'] },
  { key: 'health', label: 'Health', tags: ['Health', 'MedicalAstrology', 'OrganHealth', 'Disease', 'WellBeing', 'Lifespan'] },
  { key: 'spirit', label: 'Spirituality', tags: ['Spirituality', 'Religion', 'Pilgrimage', 'Destiny', 'HouseYoga'] },
  { key: 'life', label: 'Life Events', tags: ['Travel', 'Migration', 'Lifestyle', 'Adversity', 'Conflict'] },
]

export default function ReportsTab({ onSwitch, data, roles }) {
  const consultations = data?.consultations ?? []
  const consultationsLoading = Boolean(data?.isLoading)
  const completed = consultations.filter((c) => String(c.status).toLowerCase().includes('complete'))
  const canBook = canAccessTab('appoinments', roles)

  const { predictions, hasBirthDetails, profileLoading, isLoading, isError, error, refetch } = useHoroscopePredictions()
  const [group, setGroup] = useState('all')
  const [limit, setLimit] = useState(PAGE_SIZE)

  const activeTags = REPORT_GROUPS.find((g) => g.key === group)?.tags
  const filtered = useMemo(
    () => (activeTags ? predictions.filter((p) => p.tags.some((t) => activeTags.includes(t))) : predictions),
    [predictions, activeTags],
  )
  const visible = filtered.slice(0, limit)

  return (
    <div className="space-y-6">
      {/* ── Consultation reports — completed sessions + astrologer notes ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Your Sessions</span>
            <h3 className="text-xl text-astroDark font-royal">Consultation Reports</h3>
          </div>
          {completed.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400">{completed.length} completed</span>
          )}
        </div>

        {consultationsLoading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <ReportSkeletonRow key={i} />
            ))}
          </div>
        ) : completed.length === 0 ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">📜</div>
            <p className="text-base font-semibold text-astroDark mb-1">No consultation reports yet</p>
            <p className="text-sm text-stone-400 mb-4">Reports from your completed consultations appear here.</p>
            {canBook && (
              <button
                onClick={() => onSwitch('appoinments')}
                className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
              >
                Book a Session
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {completed.map((c) => (
              <div key={c.key} className="border border-stone-100 rounded-xl p-4 hover:shadow-md hover:border-[#D4A017]/40 transition-all">
                <div className="flex gap-3 items-start">
                  <div className="text-white w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 shadow-lg bg-[#D48806] shadow-amber-600/20">
                    <span className="text-base font-bold leading-none">{c.day}</span>
                    <span className="text-[8px] uppercase tracking-widest opacity-80 mt-0.5">{c.mon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-stone-900 leading-tight truncate">{c.person}</p>
                        <p className="text-[11px] text-stone-500 mt-0.5 truncate">{c.svc} · <span className="text-[#A67C00]">{c.code}</span></p>
                      </div>
                      <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${c.cls}`}>{c.status}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-stone-400 uppercase tracking-wide font-bold">
                      <span>🗓 {c.dateLabel}</span>
                      {c.time ? <span>🕐 {c.time}</span> : null}
                      {c.mode ? <span>{c.mode}</span> : null}
                      {c.amount ? <span className="text-[#A67C00]">{c.amount}</span> : null}
                    </div>
                  </div>
                </div>
                {c.note && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-stone-400 mb-1">Astrologer&rsquo;s Report</p>
                    <p className="text-xs text-stone-600 leading-relaxed italic">&ldquo;{c.note}&rdquo;</p>
                  </div>
                )}
                {c.id != null && (
                  <div className="flex mt-3 pt-3 border-t border-stone-100">
                    <a
                      href={appUrl('jyotish', `/bookings/${c.id}`)}
                      className="border border-stone-200 px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
                    >
                      View Full Report
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Birth chart analysis — VedAstro HoroscopePredictions ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Birth Chart</span>
            <h3 className="text-xl text-astroDark font-royal">Birth Chart Analysis</h3>
          </div>
          {hasBirthDetails && !isLoading && !isError && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400">{filtered.length} insights</span>
          )}
        </div>

        {profileLoading || isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <PredictionSkeletonCard key={i} />
            ))}
          </div>
        ) : !hasBirthDetails ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🪐</div>
            <p className="text-base font-semibold text-astroDark mb-1">Add your birth details</p>
            <p className="text-sm text-stone-400 mb-4">Your date of birth unlocks a full Vedic analysis of your chart — over a hundred classical readings.</p>
            <button
              onClick={() => onSwitch('myprofile')}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              Complete My Profile
            </button>
          </div>
        ) : isError ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t compute your report</p>
            <p className="text-sm text-stone-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Life-area filter chips */}
            <div className="flex flex-wrap gap-2 mb-5">
              {[{ key: 'all', label: 'All' }, ...REPORT_GROUPS].map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => { setGroup(g.key); setLimit(PAGE_SIZE) }}
                  className={`text-[10px] font-bold uppercase tracking-wider px-3.5 py-2 rounded-full border transition-colors ${
                    group === g.key ? 'bg-[#3D0A0A] text-white border-[#3D0A0A]' : 'border-stone-200 text-stone-500 hover:bg-stone-50'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="py-10 text-center">
                <div className="text-4xl mb-3 opacity-40">✨</div>
                <p className="text-base font-semibold text-astroDark mb-1">Nothing under this area</p>
                <p className="text-sm text-stone-400">Your chart has no classical readings in this life area — try another filter.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visible.map((p) => (
                    <div key={p.name} className="border border-stone-100 rounded-xl p-4 hover:shadow-md hover:border-[#D4A017]/40 transition-all flex flex-col">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <p className="text-sm font-bold text-stone-900 leading-tight">{humaniseName(p.name)}</p>
                        {p.tags[0] && (
                          <span className="text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">{readableTag(p.tags[0])}</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 leading-relaxed">{p.text}</p>
                      {(p.planets.length > 0 || p.houses.length > 0) && (
                        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-stone-100">
                          {p.planets.map((pl) => (
                            <span key={pl} className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-stone-50 border border-stone-100 text-stone-500">✦ {pl}</span>
                          ))}
                          {p.houses.map((h) => (
                            <span key={h} className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-stone-50 border border-stone-100 text-stone-500">{String(h).replace('House', 'House ')}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {filtered.length > limit && (
                  <div className="text-center mt-5">
                    <button
                      type="button"
                      onClick={() => setLimit((n) => n + PAGE_SIZE)}
                      className="border border-stone-200 px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
                    >
                      Show More ({filtered.length - limit} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
            <p className="text-[10px] text-stone-400 mt-5">
              Classical Vedic readings computed live from your birth details · Powered by VedAstro
            </p>
          </>
        )}
      </div>
    </div>
  )
}

/* Pulsing placeholder matching one consultation-report row. */
function ReportSkeletonRow() {
  return (
    <div className="border border-stone-100 rounded-xl p-4 animate-pulse">
      <div className="flex gap-3 items-start">
        <div className="w-14 h-14 rounded-xl bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-3.5 w-36 bg-stone-100 rounded-full" />
          <div className="h-2.5 w-52 bg-stone-100 rounded-full" />
          <div className="h-2.5 w-44 bg-stone-100 rounded-full" />
        </div>
        <div className="h-5 w-16 bg-stone-100 rounded-full shrink-0" />
      </div>
    </div>
  )
}

/* Pulsing placeholder matching one prediction card. */
function PredictionSkeletonCard() {
  return (
    <div className="border border-stone-100 rounded-xl p-4 animate-pulse space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="h-3.5 w-32 bg-stone-100 rounded-full" />
        <div className="h-4 w-16 bg-stone-100 rounded-full" />
      </div>
      <div className="h-2.5 w-full bg-stone-100 rounded-full" />
      <div className="h-2.5 w-5/6 bg-stone-100 rounded-full" />
      <div className="h-2.5 w-2/3 bg-stone-100 rounded-full" />
    </div>
  )
}
